import { useCallback, useEffect, useRef, useState } from 'react';
import { AttentionData, HomeData, PeriodSel, fetchAttention, fetchHome, periodQuery } from './homeApi';

/**
 * Δεδομένα της Αρχικής: home για την επιλεγμένη περίοδο + attention, ξανά σε κάθε αλλαγή περιόδου και
 * κάθε 60″ όσο η σελίδα φαίνεται (κρυφή καρτέλα → καμία κλήση· στην επιστροφή, αμέσως αν πέρασε το λεπτό).
 * Στην αλλαγή περιόδου τα παλιά νούμερα μένουν (αχνά) ώσπου να έρθουν τα νέα — όχι «άδειασμα».
 * Απάντηση που άργησε για παλιά επιλογή αγνοείται.
 */

export const REFRESH_MS = 60_000;

export interface HomeDataState {
  /** Τα τελευταία νούμερα (ίσως άλλης περιόδου όσο φορτώνει η νέα — βλ. `current`). */
  data: HomeData | null;
  /** Τα `data` είναι της επιλεγμένης περιόδου. */
  current: boolean;
  loading: boolean;
  error: Error | null;
  /** Πότε ήρθαν τα `data` (ms). */
  updatedAt: number | null;
  attention: AttentionData | null;
  reload: () => void;
}

const isAbort = (e: unknown): boolean => (e as { name?: string })?.name === 'AbortError';

const visible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

export function useHomeData(storeId: number | undefined, sel: PeriodSel, active = true): HomeDataState {
  const selKey = periodQuery(sel);
  const [data, setData] = useState<HomeData | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [attention, setAttention] = useState<AttentionData | null>(null);

  const reqId = useRef(0);
  const homeCtl = useRef<AbortController | null>(null);
  const attCtl = useRef<AbortController | null>(null);
  const lastLoad = useRef(0);
  const selRef = useRef(sel);
  selRef.current = sel;

  const loadHome = useCallback(async () => {
    if (!storeId) return;
    const id = ++reqId.current;
    const key = periodQuery(selRef.current);
    homeCtl.current?.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    homeCtl.current = ctl;
    lastLoad.current = Date.now();
    setLoading(true);
    try {
      const next = await fetchHome(storeId, selRef.current, ctl?.signal);
      if (id !== reqId.current) return;
      setData(next);
      setDataKey(key);
      setError(null);
      setUpdatedAt(Date.now());
    } catch (e) {
      if (id !== reqId.current || isAbort(e)) return;
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [storeId]);

  const loadAttention = useCallback(async () => {
    if (!storeId) return;
    attCtl.current?.abort();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    attCtl.current = ctl;
    try {
      setAttention(await fetchAttention(storeId, ctl?.signal));
    } catch {
      // Η «Προσοχή» είναι βοηθητική: σε σφάλμα μένει η τελευταία γνωστή εικόνα.
    }
  }, [storeId]);

  // Αλλαγή καταστήματος: τίποτα από το προηγούμενο.
  useEffect(() => {
    setData(null);
    setDataKey(null);
    setError(null);
    setUpdatedAt(null);
    setAttention(null);
  }, [storeId]);

  // Αλλαγή περιόδου (και πρώτη φόρτωση).
  useEffect(() => {
    if (!active) return;
    loadHome();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- κλειδί είναι η επιλογή, όχι το αντικείμενο
  }, [selKey, loadHome, active]);

  useEffect(() => {
    if (!active) return;
    loadAttention();
  }, [loadAttention, active]);

  // Κάθε 60″ όσο φαίνεται· στην επιστροφή σε ορατή καρτέλα αμέσως, αν πέρασε το λεπτό.
  useEffect(() => {
    if (!active || !storeId) return undefined;
    const tick = () => {
      if (!visible()) return;
      loadHome();
      loadAttention();
    };
    const timer = setInterval(tick, REFRESH_MS);
    const onVisibility = () => {
      if (visible() && Date.now() - lastLoad.current >= REFRESH_MS) tick();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, storeId, loadHome, loadAttention]);

  useEffect(() => () => {
    homeCtl.current?.abort();
    attCtl.current?.abort();
  }, []);

  const reload = useCallback(() => {
    loadHome();
    loadAttention();
  }, [loadHome, loadAttention]);

  return { data, current: dataKey === selKey, loading, error, updatedAt, attention, reload };
}
